import React, {useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {ErrorMessage as FormikErrorMessage, Formik} from 'formik';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import AddressEntry from '../../../components/ui-controls/address/AddressEntry';
import Button from '../../../components/ui-controls/Button';
import DateOfBirthInput from '../../../components/ui-controls/DateOfBirthInput';
import TextInput from '../../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Text from '../../../components/ui-controls/Text';
import Checkbox from '../../../components/ui-controls/web/CheckBox';
import useIsLoggedIn from '../../../hooks/useIsLoggedIn';
import {AppRoute} from '../../../models/AppRoute';
import {Address, User} from '../../../models/mfl';
import {
  AccountScreenAction,
  RouteNavigatorScreenProps,
} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import {Analytics} from '../../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../../utils/analytics/events';
import TogglePasswordVisibleIconButton from '../components/TogglePasswordVisibleIconButton';

import {styles} from './styles';
import {
  CreateAccountSchema,
  passwordOptional,
  passwordRequired,
} from './validationSchema';

const ErrorMessage = ({name}: {name: string}) => (
  // @ts-ignore
  <FormikErrorMessage component={ValidationMessage} name={name} />
);

const AccountCreateScreen = observer(
  ({
    route,
  }: RouteNavigatorScreenProps<
    | AppRoute.ACCOUNT_CREATE
    | AppRoute.ACCOUNT_DETAIL
    | AppRoute.ACCOUNT_CHANGE_PASSWORD
  >) => {
    const {userStore} = useStores();
    const isLoggedIn = useIsLoggedIn();

    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const [isWeakPasswordCheckboxVisible, setWeakPasswordCheckboxVisible] =
      useState(false);
    const [isSubmitting, setSubmitting] = useState(false);

    const action = route.params?.action;
    const email = route.params?.email;

    useEffect(() => {
      setWeakPasswordCheckboxVisible(userStore.showWeakPasswordCheckbox);
    }, [userStore.showWeakPasswordCheckbox]);

    const handleTogglePasswordVisible = () =>
      setPasswordVisible(!isPasswordVisible);

    const submitSuccessCallback = () => {
      setSubmitting(false);
      if (password.current) {
        // @ts-ignore
        password.current.clear();
      }
      userStore.afterLogin();
    };

    const submitErrorCallback = () => setSubmitting(false);

    const onSubmit = async (values: User) => {
      setSubmitting(true);
      if (isLoggedIn) {
        await userStore.updateUserInfo(
          action,
          values,
          submitSuccessCallback,
          submitErrorCallback,
        );
      } else {
        const successCallback = () => {
          submitSuccessCallback();
          Analytics.logEvent({
            eventName: EventName.SignUp,
            parameters: {
              screen_name: EventScreenName.Account,
              source_type: EventSourceType.Button,
              method: 'email',
            },
          });
        };
        await userStore.createUser(
          values,
          successCallback,
          submitErrorCallback,
        );
      }
    };

    const password = useRef<HTMLInputElement>(null);

    const isCreateOrUpdate =
      action === AccountScreenAction.CREATE ||
      action === AccountScreenAction.UPDATE;
    const isCreateOrChangePassword =
      action === AccountScreenAction.CREATE ||
      action === AccountScreenAction.CHANGE_PASSWORD;

    const validationSchema = isCreateOrChangePassword
      ? CreateAccountSchema.concat(passwordRequired)
      : CreateAccountSchema.concat(passwordOptional);

    type CreateAccountValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: CreateAccountValues = {
      firstName: userStore.firstName,
      lastName: userStore.lastName,
      email: email || userStore.emailAddress,
      dateOfBirth: userStore.dateOfBirth,
      phoneNumber: userStore.phoneNumber,
      billingAddress: userStore.billingAddress,
      password: '',
      passwordValidationBypass: false,
    };

    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white'}}
        keyboardShouldPersistTaps="always">
        <Formik<CreateAccountValues>
          initialValues={defaultValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}>
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            initialValues,
            setFieldValue,
            setFieldError,
            setFieldTouched,
            touched,
            values,
          }) => {
            const handleSubmitPress = () => {
              setFieldTouched('billingAddress', true);
              handleSubmit();
            };
            const renderErrorMessageWhenTouchedOrPlaceholder = (
              name: keyof CreateAccountValues,
            ) => (
              <Text style={styles.errorMessage}>
                {touched[name] && errors[name] ? (errors[name] as string) : ' '}
              </Text>
            );

            const handleAddressChange = (value: Address) => {
              setFieldValue('billingAddress', value);
            };

            return (
              <ContentView>
                {!!isCreateOrUpdate && (
                  <>
                    <TextInput
                      accessibilityLabel="First name"
                      autoCapitalize="words"
                      autoComplete="name-given"
                      autoFocus={true}
                      errorMessageWhenTouched={errors.firstName}
                      keyboardType="default"
                      label="First Name"
                      maxLength={50}
                      onBlur={handleBlur('firstName')}
                      onChangeText={handleChange('firstName')}
                      touched={touched.firstName}
                      value={values.firstName}
                    />
                    <TextInput
                      accessibilityLabel="Last name"
                      autoCapitalize="words"
                      autoComplete="name-family"
                      defaultValue={initialValues.lastName}
                      errorMessageWhenTouched={errors.lastName}
                      keyboardType="default"
                      label="Last Name"
                      maxLength={50}
                      onBlur={handleBlur('lastName')}
                      onChangeText={handleChange('lastName')}
                      touched={touched.lastName}
                      value={values.lastName}
                    />
                    <TextInput
                      accessibilityLabel="Email"
                      autoCapitalize="none"
                      autoComplete="email"
                      defaultValue={initialValues.email}
                      disabled={true}
                      editable={false}
                      errorMessageWhenTouched={errors.email}
                      keyboardType="email-address"
                      label="Email Address"
                      maxLength={100}
                      onBlur={handleBlur('email')}
                      onChangeText={handleChange('email')}
                      touched={touched.email}
                    />
                  </>
                )}
                {!!isCreateOrChangePassword && (
                  <>
                    <TextInput
                      autoCapitalize="none"
                      defaultValue={initialValues.password}
                      label="Password"
                      onBlur={handleBlur('password')}
                      onChangeText={handleChange('password')}
                      ref={password}
                      secureTextEntry={!isPasswordVisible}
                      touched={touched.password}
                      errorMessageWhenTouched={
                        isWeakPasswordCheckboxVisible
                          ? undefined
                          : errors.password
                      }
                      rightIcon={
                        <TogglePasswordVisibleIconButton
                          isPasswordVisible={isPasswordVisible}
                          onPress={handleTogglePasswordVisible}
                        />
                      }
                    />
                    {!!isWeakPasswordCheckboxVisible && (
                      <View
                        style={[styles.checkboxContainer, styles.formElement]}>
                        <Checkbox
                          boxType="square"
                          checked={values.passwordValidationBypass}
                          onChange={() =>
                            setFieldValue(
                              'passwordValidationBypass',
                              !values.passwordValidationBypass,
                            )
                          }
                          style={styles.checkbox}
                        />
                        <Text style={styles.compromisedPasswordText}>
                          This password has been found in a list of passwords
                          that was leaked from other websites. By ticking this
                          box you acknowledge that you wish to continue with
                          this password.
                        </Text>
                      </View>
                    )}
                  </>
                )}

                {!!isCreateOrUpdate && (
                  <>
                    <View style={styles.dateOfBirthContainer}>
                      <Text style={styles.label}>Date Of Birth</Text>
                      <DateOfBirthInput
                        style={styles.dateOfBirthInput}
                        initialDate={defaultValues.dateOfBirth}
                        onChange={date => {
                          setFieldValue('dateOfBirth', date, false);
                          setFieldError('dateOfBirth', undefined);
                          setFieldTouched('dateOfBirth', true, false);
                        }}
                        onInvalidDate={date => {
                          setFieldValue('dateOfBirth', date, false);
                          setFieldError('dateOfBirth', 'Invalid date');
                          setFieldTouched('dateOfBirth', true, false);
                        }}
                      />
                    </View>
                    {renderErrorMessageWhenTouchedOrPlaceholder('dateOfBirth')}
                    <TextInput
                      accessibilityLabel="Phone number"
                      autoCapitalize="none"
                      autoComplete="tel"
                      defaultValue={initialValues.phoneNumber}
                      errorMessageWhenTouched={errors.phoneNumber}
                      keyboardType="phone-pad"
                      label="Phone Number"
                      maxLength={20}
                      onBlur={handleBlur('phoneNumber')}
                      onChangeText={handleChange('phoneNumber')}
                      touched={touched.phoneNumber}
                    />
                    <Text style={styles.label}>Billing Address</Text>
                    <AddressEntry
                      onChange={handleAddressChange}
                      address={values.billingAddress}
                      placeholder="Billing Address"
                    />
                    {!values.billingAddress &&
                    !!errors.billingAddress &&
                    !!touched.billingAddress ? (
                      <ValidationMessage>
                        This is a required field
                      </ValidationMessage>
                    ) : (
                      <>
                        <ErrorMessage name="billingAddress.lines" />
                        <ErrorMessage name="billingAddress.locality" />
                        <ErrorMessage name="billingAddress.administrative_area" />
                        <ErrorMessage name="billingAddress.postal_code" />
                      </>
                    )}
                  </>
                )}
                <Button
                  containerStyle={styles.button}
                  title={isLoggedIn ? 'Save' : 'Create Account'}
                  onPress={handleSubmitPress}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </ContentView>
            );
          }}
        </Formik>
      </KeyboardAwareScrollView>
    );
  },
);

export default AccountCreateScreen;
